import { Fragment, useEffect, useState } from 'react';
import SearchableSelect from '../components/SearchableSelect';
import YearSelect from '../components/YearSelect';
import { getFilterOptions } from '../api/dashboard';
import { getMonthlyQuantityPivot, exportMonthlyQuantityPivotCsv, exportMonthlyDetailedCsv } from '../api/sales';
import { formatCurrency, formatQty, formatPercent } from '../utils/format';
import { yearDateRange, detectSelectedYear } from '../utils/filters';
import { useAuth } from '../context/AuthContext';

const MAX_PAGE_SIZE = 10000;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 'All'];

// Every metric button. 'value' and 'revenue' deliberately read the same field
// (revenueByMonth) — Month-wise Analysis has never had a separate "Value" figure from
// Revenue, and the two buttons are offered anyway because the request asks for both.
const METRICS = [
  { key: 'qty', label: 'Quantity', gpOnly: false },
  { key: 'value', label: 'Value', gpOnly: false },
  { key: 'cost', label: 'Cost', gpOnly: true },
  { key: 'revenue', label: 'Revenue', gpOnly: true },
  { key: 'gp', label: 'GP', gpOnly: true },
  { key: 'gpPercent', label: 'GP %', gpOnly: true },
];

const COMPARE_OPTIONS = [
  { key: 'none', label: 'None' },
  { key: 'previousMonth', label: 'Previous Month' },
  { key: 'sameMonthLastYear', label: 'Same Month Last Year' },
];

// Every metric a row's monthly value can be read from/derived from. GP % is never
// stored — it is always gpAmount/revenue for that same cell, never an average of
// monthly percentages (same rule the rest of the GP module follows for totals).
function monthValue(row, monthKey, metric, scope = '') {
  const p = (name) => (scope ? row[scope]?.[name] : row[name]);
  if (metric === 'gpPercent') {
    const rev = p('revenueByMonth')?.[monthKey];
    const gp = p('gpAmountByMonth')?.[monthKey];
    if (rev === undefined || gp === undefined) return undefined;
    return rev ? (gp / rev) * 100 : 0;
  }
  const map =
    metric === 'qty' ? p('qtyByMonth')
    : metric === 'cost' ? p('costByMonth')
    : metric === 'gp' ? p('gpAmountByMonth')
    : p('revenueByMonth'); // value / revenue
  return map?.[monthKey];
}

function totalValue(row, metric) {
  if (metric === 'gpPercent') return row.totalRevenue ? (row.totalGpAmount / row.totalRevenue) * 100 : 0;
  if (metric === 'qty') return row.totalQty;
  if (metric === 'cost') return row.totalCost;
  if (metric === 'gp') return row.totalGpAmount;
  return row.totalRevenue; // value / revenue
}

function formatMetric(value, metric) {
  if (value === undefined || value === null) return '';
  if (metric === 'gpPercent') return formatPercent(value);
  if (metric === 'qty') return formatQty(value);
  return formatCurrency(value);
}

// Secondary comparison line under a cell: percentage-point difference for GP % (never
// relative % change — that would be misleading for a ratio metric), relative % change
// for every other metric. Missing comparison data reads "N/A", never a misleading 0%.
function compareDisplay(current, compareVal, metric) {
  if (compareVal === undefined || compareVal === null) return { text: 'N/A', tone: 'text-gray-400' };
  if (metric === 'gpPercent') {
    const diff = (current ?? 0) - compareVal;
    if (Math.abs(diff) < 0.05) return { text: '±0.0 pp', tone: 'text-gray-500' };
    return { text: `${diff > 0 ? '+' : ''}${diff.toFixed(1)} pp`, tone: diff > 0 ? 'text-emerald-600' : 'text-red-600' };
  }
  if (!compareVal) return { text: 'N/A', tone: 'text-gray-400' };
  const diff = (current ?? 0) - compareVal;
  const pct = (diff / Math.abs(compareVal)) * 100;
  if (Math.abs(pct) < 0.05) return { text: '±0.0%', tone: 'text-gray-500' };
  return { text: `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`, tone: pct > 0 ? 'text-emerald-600' : 'text-red-600' };
}

function rowKey(row) {
  return `${row.customerCode}-${row.salesmanCode}-${row.productCode}-${row.unit}`;
}

// Defaults to the current calendar year so the page opens on relevant recent data
// instead of blending every year together; "Clear filters" returns to this same view.
function defaultFilters() {
  return { customerCode: '', salesmanCode: '', productCode: '', channel: '', subChannel: '', ...yearDateRange(new Date().getFullYear()) };
}

export default function MonthWiseAnalysis() {
  const { canViewGP } = useAuth();
  const [filters, setFilters] = useState(defaultFilters);
  const [options, setOptions] = useState({ customers: [], salesmen: [], products: [], channels: [], subChannels: [], channelSubChannels: {} });
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [metric, setMetric] = useState('qty');
  const [compare, setCompare] = useState('none');
  const [expandedKey, setExpandedKey] = useState(null);
  const [data, setData] = useState({ items: [], months: [], total: 0, totals: { qty: 0, revenue: 0 }, totalsByYear: [], compareType: 'none', compareMonths: null });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingDetailed, setExportingDetailed] = useState(false);
  const [exportStatus, setExportStatus] = useState(null); // { state, kind, seconds, bytes, message }
  const limit = pageSize === 'All' ? MAX_PAGE_SIZE : pageSize;

  const visibleMetrics = METRICS.filter((m) => canViewGP || !m.gpOnly);
  const selectedYear = detectSelectedYear(filters);

  useEffect(() => {
    // Salesmen are scoped server-side to the selected Year, so a salesman with zero
    // sales that year never shows up as a filter option in the first place.
    getFilterOptions(selectedYear ? { year: selectedYear } : undefined).then((opts) => {
      setOptions(opts);
      setFilters((f) => {
        let next = f;
        // The default view opens on the current calendar year, but if that year has no
        // data at all, fall back to the most recent year that actually has data instead
        // of silently showing an empty table.
        const year = detectSelectedYear(next);
        if (year && opts.years?.length && !opts.years.includes(Number(year))) {
          next = { ...next, ...yearDateRange(opts.years[0]) };
        }
        if (next.salesmanCode && !opts.salesmen.some((s) => s.code === next.salesmanCode)) {
          next = { ...next, salesmanCode: '' };
        }
        return next;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  useEffect(() => {
    setPage(1);
    setExpandedKey(null);
  }, [filters]);

  useEffect(() => {
    if (!canViewGP && METRICS.find((m) => m.key === metric)?.gpOnly) setMetric('qty');
  }, [canViewGP, metric]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const activeParams = {
    ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(compare !== 'none' ? { compare } : {}),
  };

  useEffect(() => {
    setLoading(true);
    getMonthlyQuantityPivot({ ...activeParams, page, limit }).then((res) => {
      setData(res);
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, debouncedSearch, page, limit, compare]);

  // Shared progress/status for both exports, so the user sees what is happening while a
  // large file is built and streamed: elapsed seconds, MB received, then done or failed.
  async function runExport(kind, setBusy, fn) {
    setBusy(true);
    const started = Date.now();
    setExportStatus({ state: 'running', kind, seconds: 0, bytes: 0 });
    const timer = setInterval(() => {
      setExportStatus((s) => (s?.state === 'running' && s.kind === kind
        ? { ...s, seconds: Math.round((Date.now() - started) / 1000) } : s));
    }, 1000);
    try {
      await fn((bytes) => setExportStatus((s) => (s?.state === 'running' && s.kind === kind ? { ...s, bytes } : s)));
      setExportStatus({ state: 'done', kind, seconds: Math.round((Date.now() - started) / 1000) });
    } catch (err) {
      console.error('Export failed:', err);
      const msg = err?.response?.status === 403
        ? 'You do not have permission for this export.'
        : 'The export failed. Please try again; if it keeps failing, narrow the date range or filters.';
      setExportStatus({ state: 'error', kind, message: msg });
    } finally {
      clearInterval(timer);
      setBusy(false);
    }
  }

  function handleExport() {
    // Full-view export (unpaginated, respects filters/metric/compare) — the backend
    // streams every matching row, not just the current page.
    return runExport('current', setExporting, (onProgress) =>
      exportMonthlyQuantityPivotCsv({ ...activeParams, metric }, onProgress));
  }

  function handleExportDetailed() {
    return runExport('detailed', setExportingDetailed, (onProgress) =>
      exportMonthlyDetailedCsv(activeParams, onProgress));
  }

  // Clear the "downloaded" note after a few seconds; errors stay until dismissed.
  useEffect(() => {
    if (exportStatus?.state !== 'done') return undefined;
    const t = setTimeout(() => setExportStatus(null), 6000);
    return () => clearTimeout(t);
  }, [exportStatus]);

  function setChannel(value) {
    const availableSubChannels = value ? options.channelSubChannels?.[value] ?? [] : null;
    const subChannelStillValid = !availableSubChannels || availableSubChannels.includes(filters.subChannel);
    setFilters((f) => ({ ...f, channel: value, subChannel: subChannelStillValid ? f.subChannel : '' }));
  }

  function setYear(year) {
    setFilters((f) => ({ ...f, ...(year ? yearDateRange(year) : { from: '', to: '' }) }));
  }

  const channelItems = (options.channels || []).map((c) => ({ code: c, name: c }));
  const availableSubChannels = filters.channel
    ? options.channelSubChannels?.[filters.channel] ?? []
    : options.subChannels || [];
  const subChannelItems = availableSubChannels.map((s) => ({ code: s, name: s }));

  const totalPages = Math.max(1, Math.ceil(data.total / limit));
  const monthLabels = data.months.map((m) => ({
    key: m,
    label: new Date(`${m}-01T00:00:00`).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }),
  }));
  const comparing = data.compareType && data.compareType !== 'none';

  // Months grouped by year, each followed by its own "Total <year>" subtotal column —
  // e.g. Jan 2025 ... Dec 2025, Total 2025, then Jan 2026 ... (rather than one grand
  // total blended across every year in the range).
  const yearGroups = [];
  for (const m of monthLabels) {
    const year = m.key.slice(0, 4);
    const current = yearGroups[yearGroups.length - 1];
    if (!current || current.year !== year) yearGroups.push({ year, months: [m] });
    else current.months.push(m);
  }
  const multiYear = yearGroups.length > 1;

  function yearSubtotal(row, months) {
    return months.reduce((sum, m) => sum + (monthValue(row, m.key, metric) || 0), 0);
  }

  const monthColumnCount = monthLabels.length + yearGroups.length + (multiYear ? 1 : 0);
  const fixedColumnCount = 8; // expand toggle, Customer, Channel, Sub-Channel, Salesman, Part Number, Description, Unit

  const metricLabel = METRICS.find((m) => m.key === metric)?.label ?? 'Quantity';

  return (
    <div>
      <h1 className="text-xl font-semibold text-gray-800 mb-4">Month-wise Sales Analysis</h1>
      <p className="text-sm text-gray-600 mb-4">
        Quantity, Value, Cost, Revenue, GP or GP % sold per Customer + Salesman + Product, broken out by
        month. Pick one metric below to keep the table compact, click a row to see every metric for that
        product together, and turn on Compare to see month-over-month or year-over-year change.
      </p>

      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 flex flex-wrap gap-4 items-end">
        <YearSelect years={options.years} value={detectSelectedYear(filters)} onChange={setYear} />
        <SearchableSelect
          label="Customer"
          value={filters.customerCode}
          onChange={(v) => setFilters((f) => ({ ...f, customerCode: v }))}
          items={options.customers}
        />
        <SearchableSelect
          label="Salesman"
          value={filters.salesmanCode}
          onChange={(v) => setFilters((f) => ({ ...f, salesmanCode: v }))}
          items={options.salesmen}
        />
        <SearchableSelect
          label="Product"
          value={filters.productCode}
          onChange={(v) => setFilters((f) => ({ ...f, productCode: v }))}
          items={options.products}
        />
        <SearchableSelect label="Channel" value={filters.channel} onChange={setChannel} items={channelItems} />
        <SearchableSelect
          label="Sub-Channel"
          value={filters.subChannel}
          onChange={(v) => setFilters((f) => ({ ...f, subChannel: v }))}
          items={subChannelItems}
        />
        <button
          onClick={() => setFilters(defaultFilters())}
          className="text-xs text-blue-600 hover:underline mb-1.5"
        >
          Clear filters
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 flex flex-wrap gap-6">
        {selectedYear || data.totalsByYear.length <= 1 ? (
          <>
            <div>
              <div className="text-xs font-medium text-gray-500 mb-0.5">
                Total Quantity{selectedYear ? ` (${selectedYear})` : ''}
              </div>
              <div className="text-2xl font-semibold tracking-tight text-violet-600">{formatQty(data.totals.qty)}</div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500 mb-0.5">
                Total Value{selectedYear ? ` (${selectedYear})` : ''}
              </div>
              <div className="text-2xl font-semibold tracking-tight text-blue-600">{formatCurrency(data.totals.revenue)}</div>
            </div>
            {canViewGP && (
              <>
                <div>
                  <div className="text-xs font-medium text-gray-500 mb-0.5">
                    Total Cost{selectedYear ? ` (${selectedYear})` : ''}
                  </div>
                  <div className="text-2xl font-semibold tracking-tight text-orange-600">{formatCurrency(data.totals.cost)}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500 mb-0.5">
                    Total GP{selectedYear ? ` (${selectedYear})` : ''}
                  </div>
                  <div className="text-2xl font-semibold tracking-tight text-emerald-600">{formatCurrency(data.totals.gpAmount)}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500 mb-0.5">
                    GP %{selectedYear ? ` (${selectedYear})` : ''}
                  </div>
                  <div className="text-2xl font-semibold tracking-tight text-emerald-700">
                    {formatPercent(data.totals.revenue ? (data.totals.gpAmount / data.totals.revenue) * 100 : 0)}
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          // "All years" with data spanning more than one year — a single blended total
          // would mix unrelated years together, so show each year's own total instead.
          data.totalsByYear.map((y) => (
            <div key={y.year} className="flex gap-4">
              <div>
                <div className="text-xs font-medium text-gray-500 mb-0.5">{y.year} Quantity</div>
                <div className="text-2xl font-semibold tracking-tight text-violet-600">{formatQty(y.qty)}</div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-500 mb-0.5">{y.year} Value</div>
                <div className="text-2xl font-semibold tracking-tight text-blue-600">{formatCurrency(y.revenue)}</div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 px-4 pt-3 text-xs text-gray-500">
          <span className="inline-block w-3 h-3 rounded bg-amber-200 border border-amber-300" />
          Highlighted rows: this product is sold to this customer in more than one unit — each unit is kept as its own row, never combined.
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search customer, part number, description, salesman…"
            className="border border-gray-300 rounded px-3 py-1.5 text-sm w-72"
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <div className="text-[11px] font-medium text-gray-500 mb-1">Metric</div>
              <div className="flex rounded border border-gray-300 overflow-hidden text-sm">
                {visibleMetrics.map((m, i) => (
                  <button
                    key={m.key}
                    onClick={() => setMetric(m.key)}
                    className={`px-3 py-1.5 whitespace-nowrap ${i > 0 ? 'border-l border-gray-300' : ''} ${
                      metric === m.key ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[11px] font-medium text-gray-500 mb-1">Compare</div>
              <select
                value={compare}
                onChange={(e) => setCompare(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1.5 text-sm"
              >
                {COMPARE_OPTIONS.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExport}
              disabled={exporting || data.total === 0}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
              title="Exports every row matching the current filters, metric and comparison — not just this page"
            >
              {exporting && <Spinner />}
              {exporting ? 'Exporting…' : 'Export Current View'}
            </button>
            <button
              onClick={handleExportDetailed}
              disabled={exportingDetailed || data.total === 0}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
              title="Excel file: one row per customer/product, months as columns under one header per metric"
            >
              {exportingDetailed && <Spinner />}
              {exportingDetailed ? 'Preparing Excel…' : 'Export Detailed Data'}
            </button>
            <span className="text-xs text-gray-500 whitespace-nowrap">{data.total} rows</span>
          </div>
        </div>

        {exportStatus && <ExportStatusBar status={exportStatus} rows={data.total} onClose={() => setExportStatus(null)} />}

        <div className="overflow-auto max-h-[560px]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-left sticky top-0 z-10">
              <tr>
                <th className="px-2 py-2 font-medium w-8" />
                <th className="px-4 py-2 font-medium">Customer</th>
                <th className="px-4 py-2 font-medium">Channel</th>
                <th className="px-4 py-2 font-medium">Sub-Channel</th>
                <th className="px-4 py-2 font-medium">Salesman</th>
                <th className="px-4 py-2 font-medium">Part Number</th>
                <th className="px-4 py-2 font-medium">Description</th>
                <th className="px-4 py-2 font-medium">Unit</th>
                {yearGroups.map((g) => (
                  <Fragment key={`group-${g.year}`}>
                    {g.months.map((m) => (
                      <th key={m.key} className="px-4 py-2 font-medium text-right whitespace-nowrap">
                        {m.label} {metricLabel}
                      </th>
                    ))}
                    <th className="px-4 py-2 font-medium text-right whitespace-nowrap bg-gray-100">
                      Total {g.year} {metricLabel}
                    </th>
                  </Fragment>
                ))}
                {multiYear && (
                  <th className="px-4 py-2 font-medium text-right whitespace-nowrap">Total {metricLabel}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={fixedColumnCount + monthColumnCount} className="px-4 py-6 text-center text-gray-400">
                    Loading…
                  </td>
                </tr>
              ) : data.items.length === 0 ? (
                <tr>
                  <td colSpan={fixedColumnCount + monthColumnCount} className="px-4 py-6 text-center text-gray-400">
                    No data for the selected filters
                  </td>
                </tr>
              ) : (
                data.items.map((row) => {
                  const key = rowKey(row);
                  const expanded = expandedKey === key;
                  return (
                    <Fragment key={key}>
                      <tr
                        className={`border-t border-gray-100 cursor-pointer ${row.multiUnit ? 'bg-amber-50' : ''} ${expanded ? 'bg-blue-50/40' : ''}`}
                        title={row.multiUnit ? 'This product is sold to this customer in more than one unit — quantities are kept separate per unit, not combined.' : undefined}
                        onClick={() => setExpandedKey(expanded ? null : key)}
                      >
                        <td className="px-2 py-2 text-center text-gray-400">{expanded ? '▾' : '▸'}</td>
                        <td className="px-4 py-2">{row.customerName}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{row.channel}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{row.subChannel}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{row.salesmanName}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{row.productCode}</td>
                        <td className="px-4 py-2">{row.productName}</td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <span
                            className={
                              row.multiUnit
                                ? 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 font-medium'
                                : ''
                            }
                          >
                            {row.unit}
                          </span>
                        </td>
                        {yearGroups.map((g) => (
                          <Fragment key={`g-${g.year}`}>
                            {g.months.map((m) => {
                              const value = monthValue(row, m.key, metric);
                              const cmp = comparing ? monthValue(row, m.key, metric, 'compare') : undefined;
                              const diff = comparing ? compareDisplay(value, cmp, metric) : null;
                              return (
                                <td key={m.key} className="px-4 py-2 text-right align-top">
                                  <div className="font-medium text-gray-800">{formatMetric(value, metric)}</div>
                                  {comparing && <div className={`text-[11px] leading-tight ${diff.tone}`}>{diff.text}</div>}
                                </td>
                              );
                            })}
                            <td className="px-4 py-2 text-right font-medium bg-gray-50 align-top">
                              {formatMetric(yearSubtotal(row, g.months), metric)}
                            </td>
                          </Fragment>
                        ))}
                        {multiYear && (
                          <td className="px-4 py-2 text-right font-medium align-top">{formatMetric(totalValue(row, metric), metric)}</td>
                        )}
                      </tr>
                      {expanded && (
                        <tr className="border-t border-blue-100 bg-blue-50/40">
                          <td colSpan={fixedColumnCount + monthColumnCount} className="px-6 py-4">
                            <ProductDetail row={row} months={monthLabels} comparing={comparing} compareType={data.compareType} canViewGP={canViewGP} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1 rounded border border-gray-300 disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-gray-500">
            Page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1 rounded border border-gray-300 disabled:opacity-40"
            >
              Next
            </button>
            <select
              value={pageSize}
              onChange={(e) => {
                const v = e.target.value;
                setPageSize(v === 'All' ? 'All' : Number(v));
                setPage(1);
              }}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm"
            >
              {PAGE_SIZE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt === 'All' ? 'Show All' : `${opt} / page`}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

// All-metrics-together view for one product row, opened by clicking that row. Reuses
// the row data the main table already fetched — no extra request, so opening a detail
// panel is instant even on a filtered set of thousands of rows.
function ProductDetail({ row, months, comparing, compareType, canViewGP }) {
  const detailMetrics = [
    { key: 'qty', label: 'Quantity' },
    { key: 'value', label: 'Value' },
    ...(canViewGP ? [
      { key: 'cost', label: 'Cost' },
      { key: 'revenue', label: 'Revenue' },
      { key: 'gp', label: 'GP' },
      { key: 'gpPercent', label: 'GP %' },
    ] : []),
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-2 border-b border-gray-100 text-xs text-gray-600 flex flex-wrap gap-x-4">
        <span><span className="font-medium text-gray-800">{row.productName}</span> ({row.productCode})</span>
        <span>Unit: {row.unit}</span>
        <span>Customer: {row.customerName}</span>
        <span>Salesman: {row.salesmanName}</span>
        {comparing && <span className="text-blue-600">Compare: {compareType === 'previousMonth' ? 'Previous Month' : 'Same Month Last Year'}</span>}
      </div>
      <div className="overflow-auto max-h-72">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-600 text-left sticky top-0">
            <tr>
              <th className="px-3 py-1.5 font-medium">Metric</th>
              {months.map((m) => (
                <th key={m.key} className="px-3 py-1.5 font-medium text-right whitespace-nowrap">{m.label}</th>
              ))}
              <th className="px-3 py-1.5 font-medium text-right whitespace-nowrap bg-gray-100">Total</th>
            </tr>
          </thead>
          <tbody>
            {detailMetrics.map((dm) => (
              <tr key={dm.key} className="border-t border-gray-100">
                <td className="px-3 py-1.5 font-medium text-gray-700 whitespace-nowrap">{dm.label}</td>
                {months.map((m) => {
                  const value = monthValue(row, m.key, dm.key);
                  const cmp = comparing ? monthValue(row, m.key, dm.key, 'compare') : undefined;
                  const diff = comparing ? compareDisplay(value, cmp, dm.key) : null;
                  return (
                    <td key={m.key} className="px-3 py-1.5 text-right align-top">
                      <div>{formatMetric(value, dm.key)}</div>
                      {comparing && <div className={`text-[10px] leading-tight ${diff.tone}`}>{diff.text}</div>}
                    </td>
                  );
                })}
                <td className="px-3 py-1.5 text-right font-medium bg-gray-50">{formatMetric(totalValue(row, dm.key), dm.key)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4 text-gray-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
      <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

const EXPORT_NAMES = { current: 'Current View (CSV)', detailed: 'Detailed Data (Excel)' };

function ExportStatusBar({ status, rows, onClose }) {
  const name = EXPORT_NAMES[status.kind] || 'Export';
  if (status.state === 'running') {
    const mb = status.bytes ? (status.bytes / 1e6).toFixed(1) : null;
    const step = mb ? `Downloading… ${mb} MB received` : 'Building the file on the server…';
    return (
      <div className="mx-4 mb-3 flex items-center gap-3 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900" role="status" aria-live="polite">
        <Spinner />
        <div>
          <div className="font-medium">Exporting {name} — {Number(rows || 0).toLocaleString()} rows · {status.seconds}s</div>
          <div className="text-xs text-blue-800">
            {step} Large exports can take up to a minute. Keep this page open; the file downloads automatically when ready.
          </div>
        </div>
      </div>
    );
  }
  if (status.state === 'done') {
    return (
      <div className="mx-4 mb-3 flex items-center justify-between rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900" role="status" aria-live="polite">
        <span>✓ {name} downloaded in {status.seconds}s — check your Downloads folder.</span>
        <button onClick={onClose} className="text-emerald-700 hover:text-emerald-900 px-1" aria-label="Dismiss">✕</button>
      </div>
    );
  }
  return (
    <div className="mx-4 mb-3 flex items-center justify-between rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
      <span>{name}: {status.message}</span>
      <button onClick={onClose} className="text-red-700 hover:text-red-900 px-1" aria-label="Dismiss">✕</button>
    </div>
  );
}
